import { Component, OnInit } from '@angular/core';
import { forkJoin, Observable, of as observableOf } from 'rxjs';
import { finalize, catchError } from 'rxjs/operators';
import { MediaObserver } from '@angular/flex-layout';
import * as _ from 'lodash';
import * as moment from 'moment';

import {
  DialogService,
  LanguageService,
  HelperService
} from 'src/app/services';
import { environment } from 'src/environments/environment';

import { ReportService } from '../report.service';
import { NewCustomerCountByRecipientService } from './new-customer-count-by-recipient.service';
import { NewCustomerCountByRecipientFilterComponent } from './new-customer-count-by-recipient-filter/new-customer-count-by-recipient-filter.component';

interface NewCustomerCountByPatient {
  coupon_count: number;
  fullname: string;
  month: string;
  territory: string;
}

interface Filter {
  module_code: string;
  file_code: string | null;
  web_filter: WebFilter;
  filter?: {};
}

interface WebFilter {
  from_month: number;
  to_month: number;
  from_year: number;
  to_year: number;
  campaign_id: number;
  distribution_id: number[];
  island_group_id: number[];
  area_id: number[];
  district_id: number[];
  group_filter_id: number[];
  group_type_id: number;
  limit: number;
  offset: number;
}

@Component({
  selector: 'esc-new-customer-count-by-recipient',
  templateUrl: './new-customer-count-by-recipient.component.html',
  styleUrls: ['./new-customer-count-by-recipient.component.scss']
})
export class NewCustomerCountByRecipientComponent implements OnInit {
  public listData: NewCustomerCountByPatient[] = [];
  public listLength = 0;
  public currentFilters: Filter = _.cloneDeep(INITIAL_OPTIONS);
  public pageSize = 10;
  public page = 1;
  public filterNumber: number = 0;
  public loading = {
    list: false,
    campaign: false,
    groupType: false,
    export: false,
    error: false
  };
  private networkErrMsg = this.languageService.getTranslation(
    'MSG_ERROR_NETWORK_CONNECTION',
    {}
  );

  public columns = [
    { def: 'territory', header: 'Territory' },
    { def: 'fullname', header: 'Doctor' },
    { def: 'month', header: 'Month' },
    { def: 'coupon_count', header: 'Patient Count' }
  ];

  public campaigns = [];
  public groupTypes = [];

  constructor(
    public media: MediaObserver,
    public service: NewCustomerCountByRecipientService,
    private dialogService: DialogService,
    private languageService: LanguageService,
    public helper: HelperService,
    private reportService: ReportService
  ) {}

  ngOnInit() {
    this.refresh();
  }

  downloadReport() {
    this.loading.export = true;

    const downloadFilters = _.cloneDeep(this.currentFilters);

    delete downloadFilters.web_filter.limit;
    delete downloadFilters.web_filter.offset;

    downloadFilters.filter = downloadFilters.web_filter;
    delete downloadFilters.web_filter;

    this.service
      .generate(downloadFilters)
      .pipe(
        finalize(() => {
          this.loading.export = false;
          return observableOf(null);
        })
      )
      .subscribe(
        (res: any) => {
          if (environment.showLogs) {
            console.log(res);
          }

          if (!res || res.status == 0 || res.status == 400) {
            this.dialogService.openSnackBar(this.networkErrMsg);
            return;
          } else {
            this.helper.reportGenerated();
          }
        },
        (err) => {
          if (environment.showLogs) {
            console.error(err);
          }
          if (this.listData.length == 0) {
          } else {
            this.dialogService.openSnackBar(
              this.languageService.getTranslation(
                'MSG_ERROR_NETWORK_CONNECTION',
                {}
              )
            );
          }
          return observableOf(null);
        }
      );
  }

  openFilter() {
    const dialogRef = this.dialogService.openDialog(
      NewCustomerCountByRecipientFilterComponent,
      {
        width: '500px',
        autoFocus: false,
        disableClose: false,
        data: {
          web_filter: { ...this.currentFilters.web_filter },
          campaigns: this.campaigns,
          groupTypes: this.groupTypes
        }
      }
    );

    dialogRef.afterClosed().subscribe((selectedFilters) => {
      if (!selectedFilters) return;

      const extractFilterValue = (filter: any, key: string, fallback = [0]) =>
        this.helper.reportFilterGet(_.get(filter, key), [fallback]);

      const campaign_id = selectedFilters.campaign.id;
      const group_type_id = selectedFilters.groupType.id;

      const from_month = Number(moment(selectedFilters.fromMonth).format('M'));
      const to_month = Number(moment(selectedFilters.toMonth).format('M'));

      const from_year = Number(
        moment(selectedFilters.fromMonth).format('YYYY')
      );
      const to_year = Number(moment(selectedFilters.toMonth).format('YYYY'));

      const distribution_id = extractFilterValue(
        selectedFilters,
        'distribution'
      );
      const island_group_id = extractFilterValue(
        selectedFilters,
        'island_group'
      );
      const area_id = extractFilterValue(selectedFilters, 'area');
      const district_id = extractFilterValue(selectedFilters, 'district');
      const group_filter_id = extractFilterValue(selectedFilters, 'territory');

      const newFilters = {
        from_month,
        to_month,
        from_year,
        to_year,
        campaign_id,
        distribution_id,
        island_group_id,
        area_id,
        district_id,
        group_type_id,
        group_filter_id
      };

      const { limit, offset, ...currentFiltersWithoutExtras } =
        this.currentFilters.web_filter;

      console.log(currentFiltersWithoutExtras, newFilters);

      if (this.helper.deepSortedEqual(currentFiltersWithoutExtras, newFilters))
        return;

      this.currentFilters.web_filter = {
        ...newFilters,
        limit,
        offset
      };

      this.fetchNewCustomerCountByDoctor(1);
    });
  }

  onPageChange(event: { pageIndex: number; pageSize: number }) {
    this.page = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchNewCustomerCountByDoctor(this.page);
  }

  fetchNewCustomerCountByDoctor(pageNumber: number) {
    this.loading.list = true;
    this.loading.error = false;

    this.currentFilters.web_filter.limit = this.pageSize;
    this.currentFilters.web_filter.offset = (pageNumber - 1) * this.pageSize;

    this.service
      .getList(this.currentFilters)
      .pipe(
        finalize(() => {
          this.loading.list = false;
          return observableOf(null);
        })
      )
      .subscribe(
        (res: any) => {
          if (!this.reportService.isValidResponse(res)) {
            this.handleFetchingError();
            return;
          }

          const { data = [], count = 0 } = res;
          this.listData = data;
          this.listLength = count;
        },
        (err) => {
          if (environment.showLogs) {
            console.error(err);
          }
          this.handleFetchingError();
          return observableOf(null);
        }
      );
  }

  fetchCampaigns(): Observable<any> {
    this.loading.campaign = true;

    const filters = this.currentFilters.web_filter;
    delete filters.limit;
    delete filters.offset;

    return this.service
      .filter({
        web_filter: {
          filter_type: 'campaign',
          filters,
          keyword: '',
          limit: 1,
          offset: 0
        }
      })
      .pipe(
        finalize(() => {
          this.loading.campaign = false;
          return observableOf(null);
        }),
        catchError((err) => {
          console.error(err);
          return observableOf(null);
        })
      );
  }

  fetchGroupTypes(): Observable<any> {
    this.loading.groupType = true;

    const filters = this.currentFilters.web_filter;
    delete filters.limit;
    delete filters.offset;

    return this.service
      .filter({
        web_filter: {
          filter_type: 'group_type',
          filters,
          keyword: '',
          limit: 1,
          offset: 0
        }
      })
      .pipe(
        finalize(() => {
          this.loading.groupType = false;
          return observableOf(null);
        }),
        catchError((err) => {
          console.error(err);
          return observableOf(null);
        })
      );
  }

  processData(response: any, type: 'campaigns' | 'groupTypes') {
    if (this.reportService.isValidResponse(response)) {
      this[type] = [...response.data];
    } else {
      this[type] = [];
      this.dialogService.openSnackBar(this.networkErrMsg);
    }
  }

  handleFetchingError() {
    this.loading.error = true;
    this.listData = [];
    this.listLength = 0;
    this.dialogService.openSnackBar(this.networkErrMsg);
  }

  refresh() {
    this.listData = [];
    this.listLength = 0;
    this.currentFilters = _.cloneDeep(INITIAL_OPTIONS);
    forkJoin({
      campaigns: this.fetchCampaigns(),
      groupTypes: this.fetchGroupTypes()
    }).subscribe({
      next: ({ campaigns, groupTypes }) => {
        this.processData(campaigns, 'campaigns');
        this.processData(groupTypes, 'groupTypes');
        this.currentFilters.web_filter.campaign_id = this.campaigns[0].id;
        this.currentFilters.web_filter.group_type_id = this.groupTypes[0].id;
        this.fetchNewCustomerCountByDoctor(1);
      },
      error: (err) => {
        console.error('Error fetching data:', err);
      }
    });
  }
}

const end_date = moment();
const start_date = moment(end_date).subtract(30, 'days');

const INITIAL_OPTIONS = {
  module_code: 'NPCDR',
  file_code: null,
  web_filter: {
    from_month: start_date.month() + 1,
    to_month: end_date.month() + 1,
    from_year: start_date.year(),
    to_year: end_date.year(),
    campaign_id: 0,
    distribution_id: [0],
    island_group_id: [0],
    area_id: [0],
    district_id: [0],
    group_filter_id: [0],
    group_type_id: 0,
    limit: 10,
    offset: 0
  }
};
